/// <reference path="./index.ts" />

/**
 * @module types/assets/data
 * @description Asset-related data constants including expiry thresholds, status labels, and icons.
 * Provides mappings between enum values and their display representations.
 */

import React from 'react';
import { FaCheck as FaCheck6 } from 'react-icons/fa';
import { IoRocket } from 'react-icons/io5';
import { ImCross } from 'react-icons/im';
import { RiAlertFill } from 'react-icons/ri';
import { BsFillLightningChargeFill } from 'react-icons/bs';
import { Assets as _Assets } from './types';

export namespace Assets {
  /**
   * @namespace Data
   * @description Data constants and mappings for asset status displays
   */
  export namespace Data {
    /**
     * @const ExpiryThreshold
     * @description Number of days remaining for each expiry status level.
     * Used to determine which status to display based on days until expiration.
     * @type {Record<_Assets.Types.ExpiryStatus, number>}
     */
    export const ExpiryThreshold: Record<_Assets.Types.ExpiryStatus, number> = {
      [_Assets.Types.ExpiryStatus.NORMAL]: 7,
      [_Assets.Types.ExpiryStatus.WARNING]: 2,
      [_Assets.Types.ExpiryStatus.CRITICAL]: 1,
    };

    /**
     * @const ExpiryStatusLabels
     * @description Human-readable labels for each expiry status.
     * @type {Record<_Assets.Types.ExpiryStatus, string>}
     */
    export const ExpiryStatusLabels: Record<_Assets.Types.ExpiryStatus, string> = {
      [_Assets.Types.ExpiryStatus.NORMAL]: 'Active',
      [_Assets.Types.ExpiryStatus.WARNING]: 'Expires Soon',
      [_Assets.Types.ExpiryStatus.CRITICAL]: 'Expired',
    };

    /**
     * @const ExpiryStatusIcons
     * @description React icon components for each expiry status.
     * @type {Record<_Assets.Types.ExpiryStatus, _Assets.Types.AssetIcon>}
     */
    export const ExpiryStatusIcons: Record<_Assets.Types.ExpiryStatus, _Assets.Types.AssetIcon> = {
      [_Assets.Types.ExpiryStatus.NORMAL]: <FaCheck6 />,
      [_Assets.Types.ExpiryStatus.WARNING]: <RiAlertFill />,
      [_Assets.Types.ExpiryStatus.CRITICAL]: <ImCross />,
    };

    /**
     * @const PackageStatusIcons
     * @description React icon components for each package status.
     * @type {Record<_Assets.Types.PackageStatus, _Assets.Types.AssetIcon>}
     */
    export const PackageStatusIcons: Record<_Assets.Types.PackageStatus, _Assets.Types.AssetIcon> = {
      [_Assets.Types.PackageStatus.STARTING]: <IoRocket />,
      [_Assets.Types.PackageStatus.PROCESSING]: <BsFillLightningChargeFill />,
      [_Assets.Types.PackageStatus.ACTIVE]: (
      <>
      <FaCheck6 />
      {/* <FaCheck /> */}
      </>),
      [_Assets.Types.PackageStatus.FAILED]: <ImCross />,
      [_Assets.Types.PackageStatus.EXPIRED]: <ImCross />,
      [_Assets.Types.PackageStatus.WARNING]: '⚠️',
      [_Assets.Types.PackageStatus.CRITICAL]: '🚨',
    };

    /**
     * @const PackageStatusLabels
     * @description Human-readable labels for each package status.
     * @type {Record<_Assets.Types.PackageStatus, string>}
     */
    export const PackageStatusLabels: Record<_Assets.Types.PackageStatus, string> = {
      [_Assets.Types.PackageStatus.STARTING]: 'Starting',
      [_Assets.Types.PackageStatus.PROCESSING]: 'Processing',
      [_Assets.Types.PackageStatus.ACTIVE]: 'Active',
      [_Assets.Types.PackageStatus.FAILED]: 'Failed',
      [_Assets.Types.PackageStatus.EXPIRED]: 'Expired',
      [_Assets.Types.PackageStatus.WARNING]: 'Warning',
      [_Assets.Types.PackageStatus.CRITICAL]: 'Critical',
    };
  }
}
